import React, {useContext, useRef, useState} from "react";
import SoilController from "../../controllers/SoilController";
import {dateFormat} from "../../date";
import PagedSearchTable, {PagedTableFunctions, TableColumn} from "../../components/PagedSearchTable";
import WarningPopup, {useWarningState} from "../../components/WarningPopup";
import TestSoilResponse from "../../controllers/TestSoilResponse";
import AppContext from "../../appContext";
import PermissionEnum from "../../controllers/PermissionEnum";
import Dialog from "../../components/Dialog";
import SoilApiData from "../../controllers/SoilApiData";
import {wrapLoader} from "../../wrapper";
import Success from "../../components/Success";
import ShowJsonData, {SharedJson} from "../../components/ShowJsonData";
import FarmersSampleController from "../../controllers/FarmersSampleController";

async function getTestSoilData(id: number): Promise<SharedJson> {
    const apiData = await SoilController
        .getApiData({id});
    return {
        data: apiData.data.testSoilData,
        mapped: apiData.data.mapped,
        original: apiData.data.original
    }
}
function getValues(result: TestSoilResponse, name: string): string {
    return result.result?.find(r => r.name === name)?.lab_result ?? ''
}

const resultCol: | TableColumn<TestSoilResponse>[] = [
    {
        header: 'Lab',
        row: (item) => <span>{item.lab}</span>
    }, {
        header: 'Sample Number',
        row: (item) => <span>{item.sampleNumber}</span>
    }, {
        header: <>
            Client (Farm)
            <div className="text-xs whitespace-nowrap text-gray-500">
                Block
            </div>
        </>,
        key: "clientAndFarm",
        row: (item, index) =>
            <>
                {item.clientName} ({item.farmName})
                <div className="text-xs whitespace-nowrap text-gray-500">
                    {item.blockNumber}
                </div>
            </>
    },
    {
        header: '(Ca+Mg) / K',
        row: (item) => <span>{getValues(item, "(Ca+Mg) / K")}</span>
    },
    {
        header: 'Ca',
        row: (item) => <span>{getValues(item, "Ca")}</span>
    },
    {
        header: 'Ca:Mg',
        row: (item) => <span>{getValues(item, "Ca:Mg")}</span>
    },
    {
        header: 'Density',
        row: (item) => <span>{getValues(item, "Density")}</span>
    },
    {
        header: 'K',
        row: (item) => <span>{getValues(item, "K")}</span>
    },
    {
        header: 'Mg',
        row: (item) => <span>{getValues(item, "Mg")}</span>
    },
    {
        header: 'Mg:K',
        row: (item) => <span>{getValues(item, "Mg:K")}</span>
    },
    {
        header: 'Na',
        row: (item) => <span>{getValues(item, "Na")}</span>
    },
    {
        header: 'Na:K',
        row: (item) => <span>{getValues(item, "Na:K")}</span>
    },
    {
        header: 'P Bray',
        row: (item) => <span>{getValues(item, "P Bray")}</span>
    },
    {
        header: 'S Am AC',
        row: (item) => <span>{getValues(item, "S Am AC")}</span>
    },
    {
        header: 'pH',
        row: (item) => <span>{getValues(item, "pH")}</span>
    },
    {
        header: 'pHKCL',
        row: (item) => <span>{getValues(item, "pHKCL")}</span>
    },
    {
        header: 'K(B)',
        row: (item) => <span>{getValues(item, "K(B)")}</span>
    },
    {
        header: 'Ca(B)',
        row: (item) => <span>{getValues(item, "Ca(B)")}</span>
    },
    {
        header: 'Mg(B)',
        row: (item) => <span>{getValues(item, "Mg(B)")}</span>
    },
    {
        header: 'Na(B)',
        row: (item) => <span>{getValues(item, "Na(B)")}</span>
    },
    {
        header: 'Fe(B)',
        row: (item) => <span>{getValues(item, "Fe(B)")}</span>
    },
    {
        header: 'Mn(B)',
        row: (item) => <span>{getValues(item, "Mn(B)")}</span>
    },
    {
        header: 'Zn(B)',
        row: (item) => <span>{getValues(item, "Zn(B)")}</span>
    },
    {
        header: 'Cu(B)',
        row: (item) => <span>{getValues(item, "Cu(B)")}</span>
    },
    {
        header: 'C WB',
        row: (item) => <span>{getValues(item, "C WB")}</span>
    },
    {
        header: 'SOM',
        row: (item) => <span>{getValues(item, "SOM")}</span>
    },
    {
        header: 'Exch Acid KCL',
        row: (item) => <span>{getValues(item, "Exch Acid KCL")}</span>
    },
    {
        header: 'T-Value AmAc',
        row: (item) => <span>{getValues(item, "T-Value AmAc")}</span>
    },
    {
        header: 'Acid Sat AmAc',
        row: (item) => <span>{getValues(item, "Acid Sat AmAc")}</span>
    },
    {
        header: 'S-Value AmAC',
        row: (item) => <span>{getValues(item, "S-Value AmAC")}</span>
    },
]
const SoilTab: React.FC<{
    archived: boolean,
    results: boolean,
    notCompleted: boolean
}> = (props) => {
    const context = useContext(AppContext);

    const pagedTableRef = useRef<PagedTableFunctions<TestSoilResponse>>()
    const resultTableRef = useRef<PagedTableFunctions<TestSoilResponse>>()

    const confirmationWarningState = useWarningState<number>(0)
    const restoreWarningState = useWarningState<number>(0)
    const bulkRestoreWarningState = useWarningState<number[]>([])

    const [rowId, setRowId] = useState(-1)
    const [archiveState, setArchiveState] = useState(false)
    const [selectedKeys, setSelectedKeys] = useState<number[]>([])
    
    function archiveWarning(id: number, archive: boolean) {
        confirmationWarningState.show('Are you sure you want to archive this row?', id)
    }
    
    function callerComp() {
        if (props.notCompleted) {
            return SoilController.notCompleted
        }
        if (props.archived) {
            return SoilController.archived
        }
        return SoilController.completed
    }

    function archive(id: number, state: boolean) {
        SoilController.archive({id, archive: state}).then(resp => {
            confirmationWarningState.hide();
            pagedTableRef.current?.refresh();
        }).then(() => {
            context.showSnack(<Success title={"Successfully"}/>)
        }).catch(() => {
            context.showSnack(<Success title={"Failed"}/>)
        })
    }

    function restore(id: number, state: boolean) {
        SoilController.archive({id, archive: state}).then(() => {
            restoreWarningState.hide()
            pagedTableRef.current?.refresh();
        }).then(() => {
            context.showSnack(<Success title={"Successfully"}/>)
        }).catch(() => {
            context.showSnack(<Success title={"Failed"}/>)
        })
    }

    function restoreWarning(id: number, archive: boolean) {
        setRowId(id)
        setArchiveState(archive)
        restoreWarningState.show("Are you sure you want to restore this row?", id)
    }

    function restoreSelected(ids: number[]) {
        SoilController.restoreSoil({ids}).then(() => {
            bulkRestoreWarningState.hide()
            setSelectedKeys([])
            pagedTableRef.current?.refresh();
        }).then(() => {
            context.showSnack(<Success title={"Successfully"}/>)
        }).catch(() => {
            context.showSnack(<Success title={"Failed"}/>)
        })
    }

    const col: TableColumn<TestSoilResponse>[] = [
        {
            header: "lab",
            key: "lab",
            row: (item) => item.lab
        },
        {
            header: "sample",
            key: "sample",
            row: (item) => item.sampleNumber
        },
        {
            header: <>
                Client (Farm)
                <div className="text-xs whitespace-nowrap text-gray-500">
                    Block
                </div>
            </>,
            key: "clientAndFarm",
            row: (item, index) =>
                <>
                    {item.clientName} ({item.farmName})
                    <div className="text-xs whitespace-nowrap text-gray-500">
                        {item.blockNumber}
                    </div>
                </>
        },
        {
            header: <>
                Agent
                <div className="text-xs whitespace-nowrap text-gray-500">
                    Agronomist
                </div>
            </>,
            row: (item, index) => <>
                {item.agent}
                <div className="text-xs whitespace-nowrap text-gray-500">
                    {item.agronomist}
                </div>
            </>
        },
        {
            header: <>
                Date Synced
                <div className="text-xs whitespace-nowrap text-gray-500">
                    Date Captured
                </div>
            </>,
            row: (item, index) => <>
                {dateFormat(item.inserted)}
                <div className="text-xs text-gray-500">
                    {dateFormat(item.appAdded)}
                </div>
            </>
        },
    ]

    function extendedCol(): TableColumn<TestSoilResponse>[] {
        if (props.notCompleted) {
            return col.concat(
                {
                    header: <div className='text-right'>Actions</div>,
                    row: (item) => <div className='flex items-center justify-end text-right'>
                        <ShowJsonData callback={() => getTestSoilData(item.id)}/>
                        {context.hasPermission(PermissionEnum.SubmissionsArchive) &&
                            <button className="btn-error btn-sm ml-1" onClick={_ => archiveWarning(item.id, true)}>x</button>}
                    </div>
                })
        }

        return col.concat(
            {
                header: <div className='whitespace-nowrap'>Submitted Id</div>,
                row: (item) => item.submissionJobId
            },
            {
                header: <div className='whitespace-nowrap'>Submitted On</div>,
                row: (item) => dateFormat(item.submittedOn)
            },
            props.archived ? [{
                header: <div className='whitespace-nowrap'>Archived By</div>,
                row: (item) => <>
                    {item.archivedBy}
                    <div className="text-xs text-gray-500">
                        {dateFormat(item.archivedDate)}
                    </div>
                </>
            }] : [],
            {
                header: 'Actions',
                row: (item) => <div className="flex items-center justify-end text-right">
                    <ShowJsonData callback={() => getTestSoilData(item.id)}/>
                    {props.archived ?
                        <button className="btn-primary btn-sm ml-1" onClick={_ => restoreWarning(item.id, false)}>restore
                        </button> : null}
                </div>
            }
        )
    }
    
    return <>

        {props.results
            ? <PagedSearchTable componentRef={resultTableRef}
                                call={SoilController.getSoilResults}
                                downloadExcelCall={SoilController.pagedExcel}
                                columns={resultCol} keyExtractor={i => i.id}/>
            : <>
                {props.archived && selectedKeys.length > 0 &&
                    <button className="btn-sm btn-primary m-2"
                            onClick={() => bulkRestoreWarningState.show("Are you sure you want to restore the selected rows?", selectedKeys)}>
                        Restore selected ({selectedKeys.length})
                    </button>}
                <PagedSearchTable componentRef={pagedTableRef}
                                    call={callerComp()}
                                    columns={extendedCol()}
                                    keyExtractor={i => i.id}
                                    selection={props.archived ? {selectedKeys, onSelectionChange: setSelectedKeys} : undefined}/>
            </>
        }


        <WarningPopup state={confirmationWarningState} onYes={() => archive(confirmationWarningState.state.data, true)}/>
        <WarningPopup state={restoreWarningState} onYes={() => restore(restoreWarningState.state.data, false)}/>
        <WarningPopup state={bulkRestoreWarningState} onYes={(ids) => restoreSelected(ids)}/>
    </>
} 

export default SoilTab;